import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { copyToClipboard } from '../utils/clipboard';

/**
 * Visão GLOBAL de todos os códigos gerados pelos clientes.
 * Filtros: busca livre, cliente, data inicial/final.
 * Ações: ver detalhe do cliente, copiar código, exportar CSV.
 */
const AdminCodes = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ rows: [], total: 0, totalPages: 1, summary: { total: 0, users_count: 0, lotes_count: 0 }, usersWithCodes: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [copiedId, setCopiedId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const debounceRef = useRef(null);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const fetchCodes = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const params = { page, limit, tzOffset: new Date().getTimezoneOffset() };
      if (debouncedSearch) params.q = debouncedSearch;
      if (userId) params.userId = userId;
      if (from) params.from = from;
      if (to) params.to = to;
      const r = await api.get('/admin/codes', { params });
      setData(r.data);
    } catch (e) {
      console.error('Error fetching codes:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, limit, debouncedSearch, userId, from, to]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const formatDateTime = (s) => {
    if (!s) return '—';
    const d = new Date(s.includes('T') ? s : s.replace(' ', 'T'));
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const copyCode = async (codigo, id) => {
    const ok = await copyToClipboard(codigo);
    if (ok) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setUserId('');
    setFrom('');
    setTo('');
    setPage(1);
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('tzOffset', String(new Date().getTimezoneOffset()));
      if (debouncedSearch) params.append('q', debouncedSearch);
      if (userId) params.append('userId', userId);
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      const r = await api.get(`/admin/codes/export?${params.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `codigos_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Erro ao exportar CSV');
    } finally {
      setExporting(false);
    }
  };

  const formatNumber = (n) => (n || 0).toLocaleString('pt-BR');

  const hasFilters = search || userId || from || to;

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{`
        .acd *, .acd *::before, .acd *::after { margin: 0; padding: 0; box-sizing: border-box; }
        .acd { font-family: 'Lexend', sans-serif; color: #555; background: #f5f5f5; min-height: 100vh; -webkit-font-smoothing: antialiased; }

        .acd-header {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          padding: 18px 30px; display: flex; align-items: center; gap: 20px;
          box-shadow: 0 4px 25px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 1000;
        }
        .acd-header-title { font-size: 22px; font-weight: 700; color: #fff; flex: 1; display: flex; align-items: center; gap: 10px; }
        .acd-header-title i { color: #f90; }
        .acd-header-back {
          display: flex; align-items: center; gap: 8px; color: #f90;
          cursor: pointer; transition: all 0.3s; font-weight: 500;
        }
        .acd-header-back:hover { color: #fff; transform: translateX(-3px); }

        .acd-main { max-width: 1400px; margin: 0 auto; padding: 30px 20px; }

        /* KPI cards */
        .acd-kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 22px; }
        .acd-kpi {
          background: #fff; padding: 18px 22px; border-radius: 14px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.05); border: 1px solid rgba(0,0,0,0.04);
          display: flex; align-items: center; gap: 16px;
        }
        .acd-kpi-icon {
          width: 50px; height: 50px; border-radius: 12px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; font-size: 20px;
        }
        .acd-kpi-icon.orange { background: rgba(255,153,0,0.15); color: #f90; }
        .acd-kpi-icon.blue { background: rgba(59,130,246,0.15); color: #2563eb; }
        .acd-kpi-icon.purple { background: rgba(168,85,247,0.15); color: #a855f7; }
        .acd-kpi-info h4 { font-size: 11px; color: #888; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
        .acd-kpi-info p { font-size: 24px; font-weight: 700; color: #333; }

        /* Filters card */
        .acd-card {
          background: #fff; border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid rgba(0,0,0,0.04);
          margin-bottom: 24px; overflow: hidden;
        }
        .acd-filters {
          padding: 18px 22px; border-bottom: 1px solid #eee;
          display: grid; grid-template-columns: 1.5fr 1fr 130px 130px auto auto; gap: 12px; align-items: end;
        }
        .acd-field { display: flex; flex-direction: column; gap: 6px; }
        .acd-field label {
          font-size: 11px; font-weight: 700; color: #888;
          text-transform: uppercase; letter-spacing: 0.5px;
        }
        .acd-input, .acd-select {
          padding: 10px 12px; border: 1px solid #e0e0e0; border-radius: 10px;
          font-family: 'Lexend', sans-serif; font-size: 13px; color: #444;
          background: #fff; outline: none; transition: all 0.2s;
        }
        .acd-input:focus, .acd-select:focus { border-color: #f90; box-shadow: 0 0 0 3px rgba(255,153,0,0.15); }
        .acd-input.search {
          padding-left: 38px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23999'%3E%3Cpath d='M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: 12px center; background-size: 16px;
        }
        .acd-btn {
          padding: 10px 16px; border-radius: 10px; border: 1px solid #e0e0e0;
          background: #fff; cursor: pointer; font-family: 'Lexend', sans-serif;
          font-size: 13px; font-weight: 600; color: #555;
          display: inline-flex; align-items: center; gap: 6px; transition: all 0.2s;
          height: 38px;
        }
        .acd-btn:hover { border-color: #f90; color: #f90; }
        .acd-btn.primary { background: linear-gradient(135deg, #f90, #ff6600); color: #fff; border-color: #f90; }
        .acd-btn.primary:hover { box-shadow: 0 4px 14px rgba(255,153,0,0.30); transform: translateY(-1px); }
        .acd-btn.export { background: #38a169; color: #fff; border-color: #38a169; }
        .acd-btn.export:hover { background: #2f855a; box-shadow: 0 4px 14px rgba(56,161,105,0.30); }

        .acd-toolbar {
          padding: 12px 22px; background: #fafafa; border-bottom: 1px solid #eee;
          display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px;
          font-size: 13px; color: #666;
        }
        .acd-toolbar strong { color: #f90; }

        /* Table */
        .acd-table-wrap { overflow-x: auto; }
        .acd-table { width: 100%; border-collapse: collapse; }
        .acd-table th {
          text-align: left; padding: 14px 16px; font-size: 11px; color: #888;
          font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
          background: #fafafa; border-bottom: 1px solid #eee;
          position: sticky; top: 0; z-index: 1;
        }
        .acd-table td { padding: 14px 16px; font-size: 13px; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
        .acd-table tr:last-child td { border-bottom: none; }
        .acd-table tr:hover { background: #fafbfc; }

        .acd-codigo {
          font-family: 'SF Mono', Consolas, monospace; font-size: 13px; font-weight: 700; color: #f90;
          background: rgba(255,153,0,0.06); padding: 6px 10px; border-radius: 8px;
          display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
          transition: all 0.15s; border: 1px solid transparent;
        }
        .acd-codigo:hover { background: rgba(255,153,0,0.12); border-color: rgba(255,153,0,0.30); }
        .acd-codigo.copied { background: rgba(56,161,105,0.15); color: #2f855a; border-color: rgba(56,161,105,0.30); }
        .acd-codigo .copy-ico { font-size: 10px; opacity: 0.5; }
        .acd-codigo:hover .copy-ico { opacity: 1; }

        .acd-cliente { display: flex; align-items: center; gap: 10px; }
        .acd-cliente-avatar {
          width: 28px; height: 28px; border-radius: 50%;
          background: linear-gradient(135deg, #f90, #ff6600);
          color: #fff; display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 11px; flex-shrink: 0;
        }
        .acd-cliente-name {
          color: #2563eb; font-weight: 600; cursor: pointer; font-size: 13px;
          text-decoration: none;
        }
        .acd-cliente-name:hover { text-decoration: underline; }
        .acd-cliente-id { color: #bbb; font-size: 11px; font-weight: 400; }

        .acd-rota { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #555; }
        .acd-rota-arrow { color: #f90; }
        .acd-rota strong { color: #333; }

        .acd-lote { font-family: 'SF Mono', Consolas, monospace; font-size: 11px; color: #888; max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

        .acd-empty {
          padding: 80px 20px; text-align: center; color: #999;
        }
        .acd-empty i { font-size: 56px; color: #ddd; display: block; margin-bottom: 14px; }
        .acd-empty p { font-size: 15px; margin-bottom: 12px; }

        /* Pagination */
        .acd-pagination {
          padding: 14px 22px; border-top: 1px solid #eee; background: #fafafa;
          display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px;
          font-size: 13px; color: #666;
        }
        .acd-page-buttons { display: flex; gap: 6px; align-items: center; }
        .acd-page-btn {
          min-width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e0e0e0;
          background: #fff; cursor: pointer; font-family: 'Lexend', sans-serif;
          font-size: 13px; font-weight: 600; color: #555;
          display: inline-flex; align-items: center; justify-content: center; padding: 0 10px;
        }
        .acd-page-btn:hover:not(:disabled) { border-color: #f90; color: #f90; }
        .acd-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .acd-page-btn.current { background: #f90; color: #fff; border-color: #f90; }

        .acd-loading-row { padding: 40px; text-align: center; color: #999; }
        .acd-spinner { display: inline-block; width: 24px; height: 24px; border: 3px solid #f0f0f0; border-top-color: #f90; border-radius: 50%; animation: acd-spin 0.7s linear infinite; vertical-align: middle; margin-right: 10px; }
        @keyframes acd-spin { to { transform: rotate(360deg); } }

        @media (max-width: 1280px) {
          .acd-filters { grid-template-columns: 1fr 1fr; }
          .acd-kpis { grid-template-columns: 1fr; }
        }
        @media (max-width: 640px) {
          .acd-header-title { font-size: 18px; }
          .acd-filters { grid-template-columns: 1fr; padding: 14px; }
          .acd-table th, .acd-table td { padding: 10px 12px; font-size: 12px; }
        }
      `}</style>

      <div className="acd">
        <header className="acd-header">
          <h1 className="acd-header-title"><i className="fas fa-qrcode"></i> Códigos Gerados</h1>
          <div className="acd-header-back" onClick={() => fetchCodes(true)} title="Atualizar">
            <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i> Atualizar
          </div>
          {/* Botão "Voltar" removido — navegação já fica na sidebar (AdminLayout) */}
        </header>

        <main className="acd-main">
          {/* KPIs */}
          <div className="acd-kpis">
            <div className="acd-kpi">
              <div className="acd-kpi-icon orange"><i className="fas fa-qrcode"></i></div>
              <div className="acd-kpi-info">
                <h4>{hasFilters ? 'Códigos filtrados' : 'Total de códigos'}</h4>
                <p>{formatNumber(data.summary.total)}</p>
              </div>
            </div>
            <div className="acd-kpi">
              <div className="acd-kpi-icon blue"><i className="fas fa-users"></i></div>
              <div className="acd-kpi-info">
                <h4>Clientes geradores</h4>
                <p>{formatNumber(data.summary.users_count)}</p>
              </div>
            </div>
            <div className="acd-kpi">
              <div className="acd-kpi-icon purple"><i className="fas fa-layer-group"></i></div>
              <div className="acd-kpi-info">
                <h4>Lotes distintos</h4>
                <p>{formatNumber(data.summary.lotes_count)}</p>
              </div>
            </div>
          </div>

          {/* Card com filtros + tabela */}
          <div className="acd-card">
            <div className="acd-filters">
              <div className="acd-field">
                <label>Buscar</label>
                <input
                  className="acd-input search"
                  type="text"
                  placeholder="Código, origem, destino, lote..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="acd-field">
                <label>Cliente</label>
                <select className="acd-select" value={userId} onChange={(e) => { setUserId(e.target.value); setPage(1); }}>
                  <option value="">Todos os clientes</option>
                  {data.usersWithCodes.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.username} ({u.codes})
                    </option>
                  ))}
                </select>
              </div>
              <div className="acd-field">
                <label>De</label>
                <input className="acd-input" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
              </div>
              <div className="acd-field">
                <label>Até</label>
                <input className="acd-input" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
              </div>
              <button className="acd-btn" onClick={clearFilters} disabled={!hasFilters} title="Limpar filtros">
                <i className="fas fa-eraser"></i> Limpar
              </button>
              <button className="acd-btn export" onClick={exportCSV} disabled={exporting || data.summary.total === 0}>
                <i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-file-excel'}`}></i>
                {exporting ? 'Exportando...' : 'Exportar CSV'}
              </button>
            </div>

            <div className="acd-toolbar">
              <span>
                Mostrando <strong>{data.rows.length}</strong> de <strong>{formatNumber(data.summary.total)}</strong> códigos
                {hasFilters && <span style={{ marginLeft: 8, color: '#f90', fontWeight: 600 }}>· Filtros ativos</span>}
              </span>
              <span>
                <label style={{ marginRight: 8 }}>Por página:</label>
                <select value={limit} onChange={(e) => { setLimit(parseInt(e.target.value)); setPage(1); }} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e0e0e0' }}>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </span>
            </div>

            <div className="acd-table-wrap">
              {loading ? (
                <div className="acd-loading-row"><span className="acd-spinner"></span> Carregando códigos...</div>
              ) : data.rows.length === 0 ? (
                <div className="acd-empty">
                  <i className="fas fa-qrcode"></i>
                  <p>{hasFilters ? 'Nenhum código bate com os filtros' : 'Nenhum código gerado ainda'}</p>
                  {hasFilters && <button className="acd-btn primary" onClick={clearFilters}>Limpar filtros</button>}
                </div>
              ) : (
                <table className="acd-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Cliente</th>
                      <th>Origem → Destino</th>
                      <th>Lote</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map(r => (
                      <tr key={r.id}>
                        <td>
                          <span
                            className={`acd-codigo ${copiedId === r.id ? 'copied' : ''}`}
                            onClick={() => copyCode(r.codigo, r.id)}
                            title="Clique para copiar"
                          >
                            {r.codigo}
                            <i className={`fas ${copiedId === r.id ? 'fa-check' : 'fa-copy'} copy-ico`}></i>
                          </span>
                        </td>
                        <td>
                          {r.username ? (
                            <div className="acd-cliente">
                              <div className="acd-cliente-avatar">
                                {r.username.slice(0,1).toUpperCase()}
                              </div>
                              <div>
                                <span className="acd-cliente-name" onClick={() => navigate(`/admin/users/${r.user_id}`)}>
                                  {r.username}
                                </span>
                                <div className="acd-cliente-id">#{r.user_id} · {r.email}</div>
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#bbb', fontStyle: 'italic' }}>— sem usuário</span>
                          )}
                        </td>
                        <td>
                          <div className="acd-rota">
                            <strong>{r.origem || '—'}</strong>
                            <i className="fas fa-arrow-right acd-rota-arrow"></i>
                            <strong>{r.destino || '—'}</strong>
                          </div>
                        </td>
                        <td><span className="acd-lote" title={r.lote_id}>{r.lote_id || '—'}</span></td>
                        <td style={{ whiteSpace: 'nowrap', color: '#666' }}>{formatDateTime(r.created_at || r.data_criacao)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Paginação */}
            {data.totalPages > 1 && (
              <div className="acd-pagination">
                <span>Página <strong>{page}</strong> de <strong>{data.totalPages}</strong></span>
                <div className="acd-page-buttons">
                  <button className="acd-page-btn" onClick={() => setPage(1)} disabled={page === 1}>«</button>
                  <button className="acd-page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
                  {(() => {
                    const pages = [];
                    const start = Math.max(1, page - 2);
                    const end = Math.min(data.totalPages, page + 2);
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <button
                          key={i}
                          className={`acd-page-btn ${i === page ? 'current' : ''}`}
                          onClick={() => setPage(i)}
                        >{i}</button>
                      );
                    }
                    return pages;
                  })()}
                  <button className="acd-page-btn" onClick={() => setPage(p => Math.min(data.totalPages, p + 1))} disabled={page === data.totalPages}>›</button>
                  <button className="acd-page-btn" onClick={() => setPage(data.totalPages)} disabled={page === data.totalPages}>»</button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminCodes;
